import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';

const useAnalytics = () => {
    const location = useLocation();

    useEffect(() => {
        // Send pageview with full path and search params
        const page = location.pathname + location.search;
        ReactGA.send({
            hitType: "pageview",
            page: page,
            title: document.title // Include page title for better reporting
        });
    }, [location]);
};

/**
 * Set the user ID for Google Analytics
 * Call this when a user logs in or their identity is confirmed
 */
const setAnalyticsUserId = (userId: string | null) => {
    if (userId) {
        // Set user ID for cross-session tracking
        ReactGA.set({ userId: userId });

        // Also set as a custom dimension via gtag for more detailed tracking
        if (window.gtag) {
            window.gtag('set', 'user_properties', {
                user_id: userId
            });
        }
    } else {
        // Clear user ID on logout
        ReactGA.set({ userId: undefined });

        if (window.gtag) {
            window.gtag('set', 'user_properties', {
                user_id: undefined
            });
        }
    }
};

/**
 * Set user properties for segmentation
 */
const setUserProperties = (properties: Record<string, string | number | boolean>) => {
    if (window.gtag) {
        window.gtag('set', 'user_properties', properties);
    }
};

const logEvent = (category: string, action: string, label?: string) => {
    ReactGA.event({
        category,
        action,
        label,
    });
};

const logGameCompleted = (score: number) => {
    logEvent('Game', 'Completed', `Score: ${score}`);
}

const logGameStarted = () => {
    logEvent('Game', 'Started');
}

const logQuestionAsked = (question: string) => {
    logEvent("Question", 'Asked', question);
};

const logGuessSubmitted = (guess: string) => {
    logEvent('Guess', 'Submitted', guess);
};

const logTriviaAsked = () => {
    logEvent('Trivia', 'Asked');
}

const logGuessCorrect = (targetName: string, score: number, attempts: number) => {
    logEvent('Guess', 'Correct', `${targetName} - Score: ${score} - Attempts: ${attempts}`);
}

const logGuessIncorrect = (guess: string, targetName: string) => {
    logEvent('Guess', 'Incorrect', `Guessed: ${guess} - Target: ${targetName}`);
}

const logTriviaRevealed = (triviaContent: string) => {
    logEvent('Trivia', 'Revealed', triviaContent);
}

const logABTestAssignment = (experimentName: string, variantName: string) => {
    logEvent('A/B Test', 'Assignment', `${experimentName} - ${variantName}`);
}

const logABTestConversion = (experimentName: string, variantName: string, conversionName: string, value?: number) => {
    ReactGA.event({
        category: 'A/B Test Conversion',
        action: conversionName,
        label: `${experimentName} - ${variantName}`,
        value,
    });
}

// TypeScript declaration for gtag (Google Analytics global function)
declare global {
    interface Window {
        gtag?: (
            command: 'event' | 'config' | 'set' | 'js',
            targetOrDate: string | Date,
            params?: any
        ) => void;
    }
}

export {
    useAnalytics,
    setAnalyticsUserId,
    setUserProperties,
    logQuestionAsked,
    logGuessSubmitted,
    logTriviaAsked,
    logGameCompleted,
    logGameStarted,
    logGuessCorrect,
    logGuessIncorrect,
    logTriviaRevealed,
    logABTestAssignment,
    logABTestConversion
};